import React, { createContext, useState, useContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { authAPI } from './lib/api';
import './App.css';

// Hide platform badge
(function hideBadge() {
  let hiding = false;
  const kill = () => {
    if (hiding) return;
    const el = document.getElementById('emergent-badge');
    if (el && el.style.display !== 'none') {
      hiding = true;
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('width', '0', 'important');
      el.style.setProperty('height', '0', 'important');
      setTimeout(() => { hiding = false; }, 50);
    }
  };
  kill();
  setInterval(kill, 500);
})();

import AuthPage from './pages/AuthPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import CoachChatPage from './pages/CoachChatPage';
import ProgressPage from './pages/ProgressPage';
import RoutinePage from './pages/RoutinePage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import SimulationPage from './pages/SimulationPage';
import AdminSupportPage from './pages/AdminSupportPage';
import ComparePage from './pages/ComparePage';
import PublicHomePage from './pages/public/PublicHomePage';
import PublicFeaturesPage from './pages/public/PublicFeaturesPage';
import PublicFaqPage from './pages/public/PublicFaqPage';
import PublicGuidePage from './pages/public/PublicGuidePage';
import PublicLegalPage from './pages/public/PublicLegalPage';
import PublicTipsPage from './pages/public/PublicTipsPage';
import PublicArticlePage from './pages/public/PublicArticlePage';
import NotFoundPage from './pages/public/NotFoundPage';
import Layout from './components/Layout';
import { PUBLIC_PAGES, LANGS, DEFAULT_LANG, pagePath, articleKeyFromPage } from './seo/seoConfig.mjs';

const PUBLIC_PAGE_COMPONENTS = {
  home: PublicHomePage,
  features: PublicFeaturesPage,
  guide: PublicGuidePage,
  tips: PublicTipsPage,
  faq: PublicFaqPage,
  privacy: (props) => <PublicLegalPage {...props} doc="privacy" />,
  terms: (props) => <PublicLegalPage {...props} doc="terms" />,
};

// Auth Context
export const AuthContext = createContext(null);
export const LanguageContext = createContext(null);
export const ThemeContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useLanguage = () => useContext(LanguageContext);
export const useTheme = () => useContext(ThemeContext);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[var(--lux-ink)]"><div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[var(--lux-ink)]"><div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.onboarding_complete) return <Navigate to="/dashboard" replace />;
  return children;
}

/** `/` -> app for signed-in users, otherwise the indexable French landing page. */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[var(--lux-ink)]"><div className="w-8 h-8 border-2 border-[var(--lux-gold)] border-t-transparent rounded-full animate-spin" /></div>;
  if (user) return <Navigate to={user.onboarding_complete ? '/dashboard' : '/onboarding'} replace />;
  return <Navigate to={pagePath('home', DEFAULT_LANG)} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('fr');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        authAPI.verify()
          .then(res => {
            if (res.data?.user) {
              setUser(res.data.user);
              setLanguage(res.data.user.language || 'fr');
            } else {
              localStorage.removeItem('token');
            }
          })
          .catch(() => {
            localStorage.removeItem('token');
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    } catch (e) {
      localStorage.removeItem('token');
      setLoading(false);
    }
  }, []);

  const loginUser = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setLanguage(userData.language || 'fr');
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (data) => {
    setUser(prev => ({ ...prev, ...data }));
  };

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, updateUser }}>
        <LanguageContext.Provider value={{ language, setLanguage }}>
          <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            <BrowserRouter>
              <div className="app-container">
                <Routes>
                <Route path="/" element={<RootRedirect />} />
                {Object.keys(PUBLIC_PAGES).flatMap(key => LANGS.map(lang => {
                  const articleKey = articleKeyFromPage(key);
                  const Page = articleKey ? PublicArticlePage : PUBLIC_PAGE_COMPONENTS[key];
                  if (!Page) return null;
                  return <Route key={`${key}-${lang}`} path={PUBLIC_PAGES[key].paths[lang]} element={<Page lang={lang} articleKey={articleKey} />} />;
                }))}
                <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to={user.onboarding_complete ? '/dashboard' : '/onboarding'} />} />
                <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
                <Route path="/coach" element={<ProtectedRoute><Layout><CoachChatPage /></Layout></ProtectedRoute>} />
                <Route path="/coach/:sessionId" element={<ProtectedRoute><Layout><CoachChatPage /></Layout></ProtectedRoute>} />
                <Route path="/progress" element={<ProtectedRoute><Layout><ProgressPage /></Layout></ProtectedRoute>} />
                <Route path="/routine" element={<ProtectedRoute><Layout><RoutinePage /></Layout></ProtectedRoute>} />
                <Route path="/history" element={<ProtectedRoute><Layout><HistoryPage /></Layout></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Layout><SettingsPage /></Layout></ProtectedRoute>} />
                <Route path="/simulation" element={<ProtectedRoute><Layout><SimulationPage /></Layout></ProtectedRoute>} />
                <Route path="/simulation/compare" element={<ProtectedRoute><Layout><ComparePage /></Layout></ProtectedRoute>} />
                <Route path="/simulation/:sessionId" element={<ProtectedRoute><Layout><SimulationPage /></Layout></ProtectedRoute>} />
                <Route path="/admin/support" element={<ProtectedRoute><Layout><AdminSupportPage /></Layout></ProtectedRoute>} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </div>
          </BrowserRouter>
          <Toaster position="top-right" theme={theme} />
        </ThemeContext.Provider>
        </LanguageContext.Provider>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
